import { ERROR_CODE, PATH, STORAGE_KEY } from '@/constants';
import { WORKSPACE_STORAGE_KEY } from '@/contexts/WorkspaceProvider';
import { supabase } from '@/services';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { handleShowError } from './common';

interface ApiConfig {
  baseURL: string | undefined;
}

// Workspace sync headers processing for frontend/backend synchronization
const processWorkspaceSyncHeaders = (response: AxiosResponse) => {
  try {
    const headers = response.headers;

    // Extract workspace sync headers
    const syncHeaders = {
      currentId: headers['x-workspace-current-id'],
      syncStatus: headers['x-workspace-sync-status'],
      context: headers['x-workspace-context'],
      expectedId: headers['x-workspace-expected-id'],
      clientId: headers['x-workspace-client-id'],
      action: headers['x-workspace-action'],
      mismatchType: headers['x-workspace-mismatch-type'],
      timestamp: headers['x-workspace-sync-timestamp'],
    };

    // Only process if we have sync headers
    if (!syncHeaders.currentId || !syncHeaders.syncStatus) {
      return;
    }

    const currentLocalWorkspace = typeof window !== 'undefined' ? localStorage.getItem(WORKSPACE_STORAGE_KEY) : null;

    // Log sync status for debugging
    console.log('🔄 Workspace sync headers received:', {
      local: currentLocalWorkspace,
      server: syncHeaders.currentId,
      status: syncHeaders.syncStatus,
      action: syncHeaders.action,
      context: syncHeaders.context,
    });

    // Handle workspace state mismatches
    if (syncHeaders.syncStatus === 'mismatch') {
      handleWorkspaceSyncMismatch(syncHeaders, currentLocalWorkspace);
    } else if (syncHeaders.syncStatus === 'synced') {
      // Ensure local storage matches server state
      if (currentLocalWorkspace !== syncHeaders.currentId) {
        console.log('🔄 Syncing workspace ID to match server:', syncHeaders.currentId);
        if (typeof window !== 'undefined') {
          localStorage.setItem(WORKSPACE_STORAGE_KEY, syncHeaders.currentId);
        }

        // Dispatch event for workspace context updates
        window.dispatchEvent(
          new CustomEvent('workspace-sync-corrected', {
            detail: {
              previousId: currentLocalWorkspace,
              correctedId: syncHeaders.currentId,
              context: syncHeaders.context,
              source: 'server_response',
            },
          }),
        );
      }
    }
  } catch (error) {
    console.warn('Error processing workspace sync headers:', error);
  }
};

// Handle workspace sync mismatch scenarios
const handleWorkspaceSyncMismatch = (syncHeaders: any, localWorkspaceId: string | null) => {
  console.log('🔄 Workspace sync mismatch detected:', {
    local: localWorkspaceId,
    server: syncHeaders.currentId,
    action: syncHeaders.action,
    mismatchType: syncHeaders.mismatchType,
  });

  switch (syncHeaders.action) {
    case 'sync_to_server':
      // Server is authoritative, update frontend to match
      if (syncHeaders.currentId !== localWorkspaceId) {
        if (typeof window !== 'undefined') {
          localStorage.setItem(WORKSPACE_STORAGE_KEY, syncHeaders.currentId);
        }

        // Dispatch sync event for workspace provider to handle
        window.dispatchEvent(
          new CustomEvent('workspace-sync-mismatch', {
            detail: {
              type: 'sync_to_server',
              previousId: localWorkspaceId,
              correctedId: syncHeaders.currentId,
              context: syncHeaders.context,
              mismatchType: syncHeaders.mismatchType,
              source: 'backend_correction',
            },
          }),
        );
      }
      break;

    case 'validate':
      // Both frontend and backend need validation
      window.dispatchEvent(
        new CustomEvent('workspace-sync-mismatch', {
          detail: {
            type: 'validation_needed',
            localId: localWorkspaceId,
            serverId: syncHeaders.currentId,
            expectedId: syncHeaders.expectedId,
            context: syncHeaders.context,
            mismatchType: syncHeaders.mismatchType,
            source: 'validation_required',
          },
        }),
      );
      break;

    default:
      console.warn('Unknown workspace sync action:', syncHeaders.action);
  }
};

const createApi = (config: ApiConfig): AxiosInstance => {
  const instance = axios.create({
    ...config,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add interceptors and other configuration here

  const setAuthorizationHeader = (request: AxiosRequestConfig | any, token: string) => {
    request.headers.Authorization = `Bearer ${token}`;
  };

  const onRequest = (config: AxiosRequestConfig | any) => {
    // CRITICAL FIX: Only access localStorage in browser environment
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem(STORAGE_KEY.ACCESS_TOKEN);
      token && setAuthorizationHeader(config, token);

      // Add workspace header for private API calls
      const workspaceId = localStorage.getItem(WORKSPACE_STORAGE_KEY);
      // Only add header if workspaceId exists and is not "null" or "undefined" string
      if (
        workspaceId &&
        workspaceId !== 'null' &&
        workspaceId !== 'undefined' &&
        config.url?.includes('/private/')
      ) {
        config.headers['x-workspace-id'] = workspaceId;
      }
    }

    return config;
  };

  const onRequestError = (error: AxiosError) => {
    return Promise.reject(error);
  };

  const onResponse = (response: AxiosResponse) => {
    // Process workspace sync headers from backend
    processWorkspaceSyncHeaders(response);
    return response;
  };

  const handleRetrieveNewSession = async (error: AxiosError) => {
    const originalRequest = error.config as any;
    const { data } = await supabase.auth.refreshSession();
    const { session } = data;
    const { access_token } = session || {};

    if (access_token && typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY.ACCESS_TOKEN, access_token);
      axios.defaults.headers.common['Authorization'] = 'Bearer ' + access_token;
      return instance(originalRequest);
    } else if (typeof window !== 'undefined') {
      localStorage.clear();
      window.location.pathname = PATH.LOGIN;
    }
  };

  const onResponseError = (error: AxiosError | any) => {
    if (error.isAxiosError) {
      if (error.code === ERROR_CODE.ERR_NETWORK) {
        console.log('error', ERROR_CODE.ERR_NETWORK);
      }

      switch (error.response?.status) {
        case ERROR_CODE.UNAUTHORIZED:
          handleRetrieveNewSession(error);
          break;

        case ERROR_CODE.UNPROCESSABLE_ENTITY:
          handleShowError(error?.response);
          break;

        case ERROR_CODE.INTERNAL_SERVER_ERROR:
          if (error?.config?.method === 'get') {
            handleShowError(error?.response);
          }
          break;

        case ERROR_CODE.NOT_FOUND:
          handleShowError(error?.response);
          break;

        default:
          return Promise.reject(error.response);
      }
    }

    return Promise.reject(error.response);
  };

  instance.interceptors.request.use(onRequest, onRequestError);
  instance.interceptors.response.use(onResponse, onResponseError);

  return instance;
};

export const apiInstance = createApi({
  baseURL: process.env.NEXT_PUBLIC_API_ENDPOINT,
});
